import React from 'react'
import { GatsbyImage, IGatsbyImageData, StaticImage } from 'gatsby-plugin-image'
import { MdMoney, MdSmartphone, MdSupportAgent, MdVerified } from 'react-icons/md'

import { useAnalytics } from 'hooks'
import Container from 'components/Container'
import Button from 'components/Button'
import { classNames } from 'services/string'
import AppStoreBadges from 'components/AppStoreBadges'

const featuredTopics = [
  [
    {
      icon: <MdMoney size={22} />,
      label: 'Planos que cabem no seu bolso',
    },
    {
      icon: <MdVerified size={22} />,
      label: 'Sua empresa 100% regularizada',
    },
  ],
  [
    {
      icon: <MdSmartphone size={22} />,
      label: 'Controle sua empresa pelo aplicativo',
    },
    {
      icon: <MdSupportAgent size={22} />,
      label: 'Atendimento personalizado',
    },
  ],
]
export interface HeroProps {
  image?: IGatsbyImageData
  title?: string | null
  subtitle?: string | null
  description?: string | null
  primaryCtaLabel?: string | null
  secondaryCtaLabel?: string | null
  highlightTopics?: { item: string | null | undefined }[] | null
  hideFeatures?: boolean
  showAppStoreBadges?: boolean
  gradient?: 'default' | 'alternative'
}

const Hero = ({
  image,
  title,
  subtitle,
  description,
  primaryCtaLabel,
  secondaryCtaLabel,
  highlightTopics = [],
  hideFeatures = false,
  showAppStoreBadges = false,
  gradient = 'default',
}: HeroProps) => {
  const { fireEvent } = useAnalytics()

  // Disparo do evento de clique em CTA
  const fireAnalyticsEvent = () => {
    fireEvent('ctaClick', {
      component: 'Hero',
      target: '#planos',
    })
  }

  const ctaLabel = primaryCtaLabel || 'Conheça nossos planos'

  const gradientStyles = {
    default: 'bg-gradient-to-br from-black from-50% md:from-45% to-black/50',
    alternative: 'bg-gradient-to-tr from-gradient-blue to-gradient-fuchsia',
  }

  return (
    <section className={classNames('w-full rounded-b-3xl', gradientStyles[gradient])}>
      <Container className='flex flex-col md:flex-row text-white gap-8 pb-0 pt-8 sm:py-12 md:py-16 sm:relative'>
        <div
          className={classNames(
            'flex flex-col xl:justify-center gap-6 text-center sm:text-left w-full md:w-2/3 lg:w-1/2',
            'sm:min-h-[350px] md:min-h-[450px] lg:min-h-[400px]',
          )}
        >
          <div className='flex flex-col gap-2'>
            <h1 className='text-primary-600 font-semibold text-3xl lg:text-4xl'>{title || 'Contabilidade completa'}</h1>
            <h2 className='text-xl'>{subtitle || 'para pequenas e médias empresas'}</h2>
          </div>

          {description && <h2 className='font-light text-md lg:text-lg'>{description}</h2>}

          {Boolean(highlightTopics?.length) && (
            <ul className='list-disc list-inside flex flex-col gap-2 mb-4'>
              {highlightTopics?.map(({ item }, index) => (
                <li className='' key={index}>
                  {item}
                </li>
              ))}
            </ul>
          )}

          {showAppStoreBadges && <AppStoreBadges className='flex justify-around sm:justify-start sm:gap-4 mt-4' />}

          <div className='flex sm:hidden flex-col gap-6'>
            <div className='flex flex-col gap-4'>
              <a className='w-full' href='#planos' onClick={() => fireAnalyticsEvent()}>
                <Button className='font-light self-start w-full'>{ctaLabel}</Button>
              </a>
              {secondaryCtaLabel && (
                <a className='w-full' href='#planos' onClick={() => fireAnalyticsEvent()}>
                  <Button className='font-light self-start w-full'>{secondaryCtaLabel}</Button>
                </a>
              )}
            </div>

            {!hideFeatures && (
              <div className='flex flex-wrap'>
                {featuredTopics.map((item, i) => (
                  <div key={i} className='w-1/2'>
                    {item.map(({ icon, label }, j) => (
                      <div
                        key={j}
                        className={`flex flex-col justify-center items-center gap-4 h-28 border-r-2 rounded-r-xl p-2 border-primary-700 bg-white/25 rounded-xl ${
                          i % 2 === 0 ? 'mr-3' : 'ml-3'
                        } ${j % 2 === 0 ? 'mb-5' : 'mt-5'}`}
                      >
                        {icon}
                        <p className='font-light text-sm'>{label}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className='hidden sm:flex flex-col gap-8'>
            {!hideFeatures && (
              <div className='flex flex-wrap gap-4'>
                {featuredTopics.map((item, i) => (
                  <div key={i} className='flex flex-col w-full gap-4'>
                    {item.map(({ icon, label }, j) => (
                      <div key={j} className='flex flex-row items-center md:gap-2 lg:gap-4'>
                        {icon}
                        <p className='font-light text-sm'>{label}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className='flex gap-4 w-full'>
              <a href='#planos' onClick={() => fireAnalyticsEvent()}>
                <Button size='lg' className='w-full font-light self-start'>
                  {ctaLabel}
                </Button>
              </a>
              {secondaryCtaLabel && (
                <a href='#planos' onClick={() => fireAnalyticsEvent()}>
                  <Button size='lg' className='w-full font-light self-start'>
                    {secondaryCtaLabel}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className='flex justify-center items-end sm:absolute sm:bottom-0 sm:right-0'>
          {image && (
            <GatsbyImage
              image={image}
              alt='Imagem de uma pessoa sorridente'
              className='w-[420px] sm:w-[320px] md:w-[420px] lg:w-[580px] xl:w-[670px] bottom-0 right-0'
              objectFit='contain'
            />
          )}
          {!image && (
            <StaticImage
              src='../../images/resources/topo-lp-4.png'
              alt='Imagem de um homem sorridente segurando um smartphone com uma versão do site da Hubs Contabilidade com uma mão enquanto aponta com a outra'
              layout='constrained'
              objectFit='contain'
              placeholder='blurred'
              loading='eager'
              className='w-[420px] sm:w-[320px] md:w-[420px] lg:w-[580px] xl:w-[670px] bottom-0 right-0'
              quality={60}
            />
          )}
        </div>
      </Container>
    </section>
  )
}

export default Hero
